import { useState, useEffect } from "react";
import { CloseOutlined, LoadingOutlined, ShoppingOutlined, CalendarOutlined } from "@ant-design/icons";
import { formatDateTime } from "../utils/orderHelpers";

const getStatusBadge = (status) => {
  const styles = {
    Pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
    Accept: "bg-blue-100 text-blue-800 border-blue-300",
    Confirmed: "bg-blue-100 text-blue-800 border-blue-300",
    Dispatch: "bg-purple-100 text-purple-800 border-purple-300",
    Delivered: "bg-green-100 text-green-800 border-green-300",
    Cancel: "bg-red-100 text-red-800 border-red-300",
  };
  return styles[status] || "bg-gray-100 text-gray-800 border-gray-300";
};

export default function PreviousOrdersModal({ 
  orderToken, 
  onClose, 
  connection 
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [previousOrders, setPreviousOrders] = useState([]);

  useEffect(() => {
    if (!connection || connection.state !== "Connected" || !orderToken) return;

    const handlePreviousOrdersResponse = (response) => {
      console.log("Previous Orders Response:", response);
      
      setIsLoading(false);
      
      if (response.dataPayload?.Success) {
        setPreviousOrders(response.dataPayload.Orders || []);
      }
    };

    connection.on("CustomerPreviousOrders", handlePreviousOrdersResponse);

    return () => {
      connection.off("CustomerPreviousOrders", handlePreviousOrdersResponse);
    };
  }, [connection, orderToken]);

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[200]"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 py-4 border-b bg-gradient-to-r from-gray-800 to-gray-900">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <CloseOutlined />
          </button>

          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ShoppingOutlined />
            Customer Order History
          </h2>
          <p className="text-sm text-gray-300 mt-1">
            {isLoading ? "Loading..." : `Last ${previousOrders.length} previous orders`}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingOutlined className="text-4xl text-blue-500 mb-4" />
              <p className="text-gray-600">Loading order history...</p>
            </div>
          ) : previousOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <ShoppingOutlined className="text-3xl text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg font-medium">No previous orders found</p>
              <p className="text-gray-400 text-sm mt-2">This customer hasn't placed any orders before</p>
            </div>
          ) : (
            <div className="space-y-3">
              {previousOrders.map((order, index) => (
                <OrderCard key={order.OrderToken || index} order={order} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order }) {
  const itemsCount = order.Items?.length || 0;
  const totalAmount = Math.round((order.AmountWithGst || 0) + (order.DeliveryCharges || 0));

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-gray-900">{order.OrderNumber}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${getStatusBadge(order.Status)}`}>
              {order.Status}
            </span>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <CalendarOutlined />
            {formatDateTime(order.OrderTime)}
          </p>
        </div>
        
        <div className="text-right">
          <p className="text-lg font-bold text-gray-900">Rs. {totalAmount.toLocaleString()}</p>
          <p className="text-xs text-gray-500">{itemsCount} item(s)</p>
        </div>
      </div>

      {/* Order Items - Compact List */}
      <div className="border-t pt-3 mt-3">
        <div className="space-y-1.5">
          {order.Items?.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 flex-1">
                <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                  {item.Quantity}
                </span>
                <span className="text-gray-700 font-medium">{item.Name}</span>
                {item.Variations?.[0]?.Size?.Name && item.Variations[0].Size.Name !== "-" && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                    {item.Variations[0].Size.Name}
                  </span>
                )}
              </div>
              <span className="text-gray-600 font-semibold">Rs. {(item.Price * item.Quantity).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-gray-500">
        <span>Branch: {order.BranchName}</span>
        {order.DeliveryCharges > 0 && (
          <span>Delivery: Rs. {order.DeliveryCharges}</span>
        )}
      </div>
    </div>
  );
}